import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardHeader, CardContent, CircularProgress, Alert, Box, Chip, Button, Snackbar } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import useApi from '../hooks/useApi';
import SendHistoryResponseModal from './SendHistoryResponseModal';
import ConfirmDialog from './ConfirmDialog';
import SendHistoryMRTTable from './SendHistoryMRTTable';
import { buildSendHistoryRows } from './SendHistory';
import { useEnrichedSendHistoryRows } from '../hooks/useEnrichedSendHistoryRows';

const SendHistoryMRT = () => {
  const { useVmtSendHistory, useDeleteVmtSendHistory } = useApi();
  const { data, isLoading, isError, error, refetch, isFetching } = useVmtSendHistory();
  const deleteHistory = useDeleteVmtSendHistory();
  const [selectedRow, setSelectedRow] = useState(null);
  const [rowSelection, setRowSelection] = useState({});
  const [confirmState, setConfirmState] = useState({ open: false, ids: [], description: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const rawRows = useMemo(() => buildSendHistoryRows(data), [data]);
  const { rows, meta } = useEnrichedSendHistoryRows(rawRows);

  useEffect(() => {
    const validIds = new Set(rows.map((row) => row.id));
    setRowSelection((prev) => {
      const next = {};
      Object.entries(prev).forEach(([key, value]) => {
        if (value && validIds.has(key)) next[key] = true;
      });
      return next;
    });
  }, [rows]);

  const openRowDetails = useCallback((row) => {
    if (!row?.__sendEntry) return;
    const { __sendEntry, ...rest } = row;
    setSelectedRow({ entry: __sendEntry, record: rest });
  }, []);

  const getEntryIdsFromRowSelection = useCallback(
    (selection) => {
      const ids = new Set();
      Object.keys(selection).forEach((rowId) => {
        const row = rows.find((item) => item.id === rowId);
        if (row?.__sendEntry?.id) ids.add(row.__sendEntry.id);
      });
      return Array.from(ids);
    },
    [rows]
  );

  const handleDeleteSelected = useCallback(() => {
    const entryIds = getEntryIdsFromRowSelection(rowSelection);
    if (entryIds.length === 0) return;
    setConfirmState({
      open: true,
      ids: entryIds,
      description: entryIds.length === 1 ? 'Delete this history entry?' : `Delete ${entryIds.length} history entries?`,
    });
  }, [getEntryIdsFromRowSelection, rowSelection]);

  const handleClearAll = useCallback(() => {
    const entryIds = Array.from(new Set(rawRows.map((row) => row.__sendEntry?.id).filter(Boolean)));
    if (entryIds.length === 0) return;
    setConfirmState({ open: true, ids: entryIds, description: 'Clear all send history entries?' });
  }, [rawRows]);

  const closeConfirmDialog = useCallback(() => {
    if (deleteHistory.isPending) return;
    setConfirmState({ open: false, ids: [], description: '' });
  }, [deleteHistory.isPending]);

  const confirmDeletion = useCallback(async () => {
    if (confirmState.ids.length === 0) return;
    try {
      await deleteHistory.mutateAsync(confirmState.ids);
      setRowSelection({});
      await refetch();
    } catch (mutationError) {
      console.error('Failed to delete history entries', mutationError);
    } finally {
      setConfirmState({ open: false, ids: [], description: '' });
    }
  }, [confirmState.ids, deleteHistory, refetch]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Card sx={{ mt: 2 }}>
        <CardHeader
          title="Send History (MRT)"
          subheader="Experimental MRT-powered table with multi-column filtering."
          action={
            <Button variant="outlined" size="small" onClick={() => refetch()} disabled={isFetching} aria-label="Refresh MRT send history">
              {isFetching ? 'Refreshing…' : 'Refresh'}
            </Button>
          }
        />
        <CardContent>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {isError && <Alert severity="error">{error?.message || 'Failed to load send history.'}</Alert>}
          {!isLoading && !isError && rows.length === 0 && <Alert severity="info">No send history records found.</Alert>}
          {!isLoading && !isError && rows.length > 0 && (
            <>
              {meta.usedCache && (
                <Box sx={{ mb: 1 }}>
                  <Chip label="Pulled from cache" color="warning" size="small" />
                </Box>
              )}
              <SendHistoryMRTTable
                rows={rows}
                onRowClick={openRowDetails}
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
                onRequestDelete={handleDeleteSelected}
                onRequestClear={handleClearAll}
                onRefresh={() => refetch()}
                isRefreshing={isFetching}
                onNotify={(message, severity = 'info') => setSnackbar({ open: true, message, severity })}
              />
            </>
          )}
        </CardContent>
        <SendHistoryResponseModal
          open={Boolean(selectedRow)}
          onClose={() => setSelectedRow(null)}
          entry={selectedRow?.entry || null}
          record={selectedRow?.record || null}
        />
        <ConfirmDialog
          open={confirmState.open}
          title="Confirm Deletion"
          description={confirmState.description}
          confirmLabel="Delete"
          confirmColor="error"
          loading={deleteHistory.isPending}
          onConfirm={confirmDeletion}
          onCancel={closeConfirmDialog}
        />
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <MuiAlert severity={snackbar.severity} variant="filled" elevation={6}>
            {snackbar.message}
          </MuiAlert>
        </Snackbar>
      </Card>
    </LocalizationProvider>
  );
};

export default SendHistoryMRT;
